import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Calendar, AlertTriangle, Moon, Zap, Activity, Flame, MessageSquare } from "lucide-react";
import AppShell from "../../components/AppShell";
import { api, formatErr } from "../../lib/api";

const DAYS = ["Lunes","Martes","Miércoles","Jueves","Viernes","Sábado","Domingo"];

const RPE_LABELS = ["Muy suave","Suave","Ligero","Cómodo","Moderado","Algo duro","Duro","Muy duro","Casi máximo","Máximo"];
const ENERGY_EMOJI = ["💀","😴","😐","🙂","🔥"];
const STRESS_EMOJI = ["😌","🙂","😐","😣","🤯"];

export default function ClientFeedback() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    workout_day: DAYS[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1],
    rpe: 7, energy: 4, pain: false, pain_zone: "", sleep_hours: 8, stress: 2, notes: "",
  });
  const [sending, setSending] = useState(false);

  const load = async () => { const { data } = await api.get("/me/feedback"); setItems(data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/me/feedback", {
        workout_day: form.workout_day,
        rpe: parseInt(form.rpe), energy: parseInt(form.energy),
        pain: !!form.pain, pain_zone: form.pain ? form.pain_zone || null : null,
        sleep_hours: parseFloat(form.sleep_hours), stress: parseInt(form.stress),
        notes: form.notes || null,
      });
      toast.success("Feedback enviado. Gracias 💪");
      setForm({ ...form, pain: false, pain_zone: "", notes: "" });
      load();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSending(false); }
  };

  const rpeColor = rpeToColor(form.rpe);

  return (
    <AppShell>
      <div className="qv-page-head" data-testid="client-feedback">
        <div className="label">Feedback de sesión</div>
        <h1>¿Cómo ha ido?</h1>
        <p className="sub">Cuéntale a tu coach cómo te has sentido tras la sesión. Esto le permite ajustar tu plan.</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 22 }} className="qv-fb-grid">
        <form onSubmit={submit} className="qv-card-app" data-testid="feedback-form">
          <div className="qv-card-head"><h3>Nueva entrada</h3></div>

          <div className="qv-field" style={{ marginBottom: 18 }}>
            <label>Día de la sesión</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {DAYS.map((d) => {
                const active = form.workout_day === d;
                return (
                  <button
                    type="button"
                    key={d}
                    onClick={() => setForm({ ...form, workout_day: d })}
                    style={{
                      background: active ? "rgba(225,29,46,0.12)" : "#060606",
                      border: `1px solid ${active ? "rgba(225,29,46,0.6)" : "rgba(255,255,255,0.08)"}`,
                      color: active ? "var(--qv-red)" : "#cfcfcf",
                      padding: "8px 12px",
                      fontSize: 11,
                      letterSpacing: "0.18em",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      cursor: "pointer",
                    }}
                    data-testid={`fb-day-${d}`}
                  >
                    {d.slice(0, 3)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* RPE */}
          <MetricBlock
            icon={Flame}
            color={rpeColor}
            label="RPE — Esfuerzo percibido"
            value={form.rpe}
            unit="/10"
            sub={RPE_LABELS[form.rpe - 1]}
          >
            <input type="range" min="1" max="10" value={form.rpe} onChange={(e) => setForm({ ...form, rpe: e.target.value })} data-testid="fb-rpe" style={sliderStyle(rpeColor)} />
          </MetricBlock>

          {/* Energy */}
          <MetricBlock
            icon={Zap}
            color="#EAB308"
            label="Energía"
            value={form.energy}
            unit="/5"
            sub={ENERGY_EMOJI[form.energy - 1] + "  Cómo te encuentras"}
          >
            <input type="range" min="1" max="5" value={form.energy} onChange={(e) => setForm({ ...form, energy: e.target.value })} data-testid="fb-energy" style={sliderStyle("#EAB308")} />
          </MetricBlock>

          {/* Stress */}
          <MetricBlock
            icon={Activity}
            color="#A855F7"
            label="Estrés mental"
            value={form.stress}
            unit="/5"
            sub={STRESS_EMOJI[form.stress - 1] + "  Carga mental del día"}
          >
            <input type="range" min="1" max="5" value={form.stress} onChange={(e) => setForm({ ...form, stress: e.target.value })} data-testid="fb-stress" style={sliderStyle("#A855F7")} />
          </MetricBlock>

          {/* Sleep */}
          <MetricBlock
            icon={Moon}
            color="#60A5FA"
            label="Horas de sueño"
            value={form.sleep_hours}
            unit="h"
            sub={sleepHint(form.sleep_hours)}
          >
            <input type="range" min="0" max="12" step="0.5" value={form.sleep_hours} onChange={(e) => setForm({ ...form, sleep_hours: e.target.value })} data-testid="fb-sleep" style={sliderStyle("#60A5FA")} />
          </MetricBlock>

          {/* Pain */}
          <div className="qv-field" style={{ marginBottom: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", textTransform: "none", letterSpacing: 0, color: "#fff", fontSize: 14 }}>
              <input type="checkbox" checked={form.pain} onChange={(e) => setForm({ ...form, pain: e.target.checked })} data-testid="fb-pain" />
              <AlertTriangle size={14} color={form.pain ? "var(--qv-red)" : "#737373"} />
              ¿Has notado dolor o molestia?
            </label>
            {form.pain && (
              <input
                value={form.pain_zone}
                onChange={(e) => setForm({ ...form, pain_zone: e.target.value })}
                placeholder="Zona del dolor (ej: rodilla derecha)"
                style={{ marginTop: 8 }}
                data-testid="fb-pain-zone"
              />
            )}
          </div>

          <div className="qv-field">
            <label>Notas (opcional)</label>
            <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Sensaciones, comentarios..." data-testid="fb-notes" />
          </div>

          <button type="submit" disabled={sending} className="qv-btn-primary" style={{ width: "100%", justifyContent: "center", marginTop: 18 }} data-testid="fb-submit">
            <Send size={14} /> {sending ? "ENVIANDO..." : "ENVIAR FEEDBACK"}
          </button>
        </form>

        <div className="qv-card-app">
          <div className="qv-card-head"><h3>Tu historial</h3></div>
          {items.length === 0 ? (
            <div className="qv-empty" style={{ padding: 30 }}>Aún no has dejado feedback</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 640, overflowY: "auto" }}>
              {items.map((f) => (
                <FeedbackRow key={f.id} f={f} />
              ))}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function MetricBlock({ icon: Icon, color, label, value, unit, sub, children }) {
  return (
    <div style={{ background: "#060606", border: `1px solid ${color}22`, padding: 14, marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Icon size={14} color={color} />
          <span style={{ fontSize: 11, color, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 700 }}>{label}</span>
        </div>
        <div style={{ fontFamily: "Bebas Neue", fontSize: "1.7rem", color, lineHeight: 1 }}>
          {value}<span style={{ fontFamily: "Inter", fontSize: 11, color: "#737373", marginLeft: 3 }}>{unit}</span>
        </div>
      </div>
      {children}
      {sub && <div style={{ fontSize: 11, color: "#a3a3a3", marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

function FeedbackRow({ f }) {
  return (
    <div style={{ padding: 14, background: "#060606", border: "1px solid rgba(255,255,255,0.05)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: "#737373", textTransform: "uppercase", letterSpacing: "0.18em" }}>
          <Calendar size={11} style={{ display: "inline", marginRight: 4 }} />
          {new Date(f.date).toLocaleDateString("es-ES")} · {f.workout_day || ""}
        </div>
        {f.pain && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--qv-red)", fontSize: 10, letterSpacing: "0.18em", fontWeight: 700, padding: "3px 8px", border: "1px solid rgba(225,29,46,0.5)", background: "rgba(225,29,46,0.06)" }}>
            <AlertTriangle size={10} /> DOLOR{f.pain_zone ? ` · ${f.pain_zone}` : ""}
          </span>
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
        <Chip color={rpeToColor(f.rpe)} label="RPE" value={`${f.rpe}/10`} />
        <Chip color="#EAB308" label="Energía" value={`${f.energy}/5`} />
        <Chip color="#60A5FA" label="Sueño" value={`${f.sleep_hours}h`} />
        <Chip color="#A855F7" label="Estrés" value={`${f.stress}/5`} />
      </div>
      {f.notes && (
        <div style={{ marginTop: 10, padding: 10, background: "rgba(255,255,255,0.02)", borderLeft: "2px solid rgba(225,29,46,0.4)", fontSize: 12, color: "#cfcfcf", display: "flex", gap: 8, alignItems: "flex-start" }}>
          <MessageSquare size={12} color="#737373" style={{ marginTop: 2, flexShrink: 0 }} />
          <span style={{ fontStyle: "italic" }}>"{f.notes}"</span>
        </div>
      )}
    </div>
  );
}

function Chip({ color, label, value }) {
  return (
    <div style={{ padding: "6px 8px", background: "rgba(255,255,255,0.02)", border: `1px solid ${color}33` }}>
      <div style={{ fontSize: 9, color, letterSpacing: "0.2em", fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontFamily: "Bebas Neue", fontSize: "1.05rem", color: "#fff", lineHeight: 1, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function rpeToColor(v) {
  const n = parseInt(v);
  if (n <= 4) return "#22C55E";
  if (n <= 6) return "#EAB308";
  if (n <= 8) return "#F97316";
  return "#E11D2E";
}

function sleepHint(h) {
  const n = parseFloat(h);
  if (n >= 8) return "Recuperación óptima";
  if (n >= 7) return "Recuperación correcta";
  if (n >= 6) return "Justo, mejorar si puedes";
  return "Insuficiente. Prioriza descanso";
}

function sliderStyle(color) {
  return {
    width: "100%",
    accentColor: color,
  };
}
